/**
 * Hook de integração com a Gamepad API do navegador.
 *
 * Permite jogar OZ Emblem integralmente com controles de videogame
 * (Xbox, PlayStation, Nintendo Switch e joysticks USB/Bluetooth).
 */

import { useEffect, useRef, useState } from 'react';

export interface GamepadCallbacks {
  readonly onDirection?: (dx: number, dy: number) => void;
  readonly onConfirm?: () => void;
  readonly onCancel?: () => void;
  readonly onCycleTarget?: (step: number) => void;
  readonly onCycleAlly?: (direction: 1 | -1) => void;
  readonly onEndPhase?: () => void;
  readonly onToggleMute?: () => void;
  readonly onSkipCombat?: () => void;
  /** Acende ou apaga o alcance do exército inimigo — o L3, no analógico. */
  readonly onToggleThreat?: () => void;
  /** Afastar (−1) ou aproximar (+1) a câmera do mapa. */
  readonly onZoomStep?: (direction: 1 | -1) => void;
  /**
   * Qualquer um dos quatro botões de rosto, sem perguntar qual.
   *
   * Para a tela em que não há escolha nenhuma a fazer — uma caixa de fala —, e
   * portanto nada que um botão possa cancelar. Prender o avanço ao "confirmar"
   * fazia com que uma preferência trocada (adaptador genérico, controle de
   * terceiros) cancelasse em vez de avançar, e a conversa inteira sumia num
   * toque. Quem usa isto NÃO deve usar `onConfirm`/`onCancel` ao mesmo tempo:
   * os dois disparariam no mesmo botão.
   */
  readonly onAnyFaceButton?: () => void;
}

export type GamepadLayout = 'xbox' | 'nintendo';

/**
 * Qual botão confirma.
 *
 * A Gamepad API normaliza os botões pela POSIÇÃO, não pelo rótulo: o índice 0 é
 * sempre o de baixo e o 1 sempre o da direita, em qualquer controle. O que muda
 * de plataforma é o costume — no Xbox e no PlayStation confirma-se com o de
 * baixo (A / ✕), na Nintendo com o da direita (A, que lá fica onde o Xbox põe o
 * B).
 *
 * Adivinhar isso pelo nome do controle erra: adaptador genérico, controle
 * de terceiros e emulador se identificam de qualquer jeito, e quando erra o
 * jogador aperta confirmar e a ação é cancelada. Então a escolha é dele, com o
 * costume mais comum no PC como ponto de partida.
 */
export type ConfirmButton = 'inferior' | 'direita';

export const CONFIRM_BUTTON_STORAGE_KEY = 'oz:gamepad:confirmar';

/** O botão de baixo, que é o que a maioria espera num PC. */
export const DEFAULT_CONFIRM_BUTTON: ConfirmButton = 'inferior';

export function loadConfirmButton(): ConfirmButton {
  try {
    const saved = window.localStorage.getItem(CONFIRM_BUTTON_STORAGE_KEY);
    return saved === 'direita' || saved === 'inferior' ? saved : DEFAULT_CONFIRM_BUTTON;
  } catch {
    // Navegador com armazenamento bloqueado: vale o padrão, e a preferência
    // dura só a sessão.
    return DEFAULT_CONFIRM_BUTTON;
  }
}

export function saveConfirmButton(value: ConfirmButton): void {
  try {
    window.localStorage.setItem(CONFIRM_BUTTON_STORAGE_KEY, value);
  } catch {
    // Idem: não poder lembrar não é motivo para não obedecer agora.
  }
}

export interface GamepadState {
  readonly isConnected: boolean;
  readonly gamepadName: string | null;
  readonly layout: GamepadLayout;
}

const DEADZONE = 0.45;
const INITIAL_REPEAT_MS = 250;
const REPEAT_INTERVAL_MS = 120;

export function useGamepad(
  callbacks: GamepadCallbacks,
  enabled = true,
  confirmButton: ConfirmButton = DEFAULT_CONFIRM_BUTTON,
): GamepadState {
  const [isConnected, setIsConnected] = useState(false);
  const [gamepadName, setGamepadName] = useState<string | null>(null);
  const [layout, setLayout] = useState<GamepadLayout>('xbox');

  // O laço de leitura é montado uma vez e enxerga o `isConnected` do primeiro
  // render para sempre; sem esta referência ele reanunciava o controle a cada
  // quadro, e a busca pelo nome do controle acontecia sessenta vezes por segundo.
  const connectedRef = useRef(false);

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // O laço de leitura é montado uma vez só e não enxerga estado que muda; a
  // preferência chega até ele por referência, e passa a valer no quadro seguinte.
  const confirmRef = useRef(confirmButton);
  confirmRef.current = confirmButton;

  useEffect(() => {
    let animId: number;
    const prevButtons = new Map<number, boolean>();
    let isInitialized = false;
    let lastDirX = 0;
    let lastDirY = 0;
    let nextRepeatTime = 0;

    const detectLayout = (id: string): GamepadLayout => {
      // Nintendo Switch Pro Controller, Joy-Cons e adaptadores USB Nintendo (Vendor 057e)
      const isNintendo = /nintendo|switch|joy-?con|pro\s*controller|057e/i.test(id);
      return isNintendo ? 'nintendo' : 'xbox';
    };

    const onConnected = (e: GamepadEvent) => {
      connectedRef.current = true;
      setIsConnected(true);
      setGamepadName(e.gamepad.id);
      setLayout(detectLayout(e.gamepad.id));
    };

    const onDisconnected = () => {
      const remaining = navigator.getGamepads ? Array.from(navigator.getGamepads()).filter(Boolean) : [];
      if (remaining.length > 0 && remaining[0]) {
        connectedRef.current = true;
        setIsConnected(true);
        setGamepadName(remaining[0].id);
        setLayout(detectLayout(remaining[0].id));
      } else {
        connectedRef.current = false;
        setIsConnected(false);
        setGamepadName(null);
      }
    };

    window.addEventListener('gamepadconnected', onConnected);
    window.addEventListener('gamepaddisconnected', onDisconnected);

    const poll = (now: number) => {
      if (typeof navigator.getGamepads === 'function') {
        const gamepads = navigator.getGamepads();
        let pad: Gamepad | null = null;
        for (let i = 0; i < gamepads.length; i++) {
          const currentPad = gamepads[i];
          if (currentPad) {
            pad = currentPad;
            break;
          }
        }

        if (pad) {
          const currentLayout = detectLayout(pad.id);
          if (!connectedRef.current) {
            connectedRef.current = true;
            setIsConnected(true);
            setGamepadName(pad.id);
            setLayout(currentLayout);
          }

          // Inicialização segura: sementa o estado dos botões no primeiro frame para que
          // botões já pressionados no momento da montagem (ex: confirmação que abriu a cena)
          // não disparem falsos cliques edge-triggered.
          if (!isInitialized) {
            for (let i = 0; i < pad.buttons.length; i++) {
              prevButtons.set(i, Boolean(pad.buttons[i]?.pressed));
            }
            isInitialized = true;
          }

          /*
           * A borda de cada botão é medida SEMPRE, mesmo com o gancho desligado.
           *
           * Antes o registro do que estava pressionado só era atualizado quando
           * o gancho estava ligado, e um botão apertado durante a pausa
           * disparava sozinho no instante em que ela terminava: o Start que
           * pulava a cena do capítulo encerrava a fase assim que a batalha
           * voltava a ouvir o controle. Medir é medir; o que o desligamento
           * suspende é o DESPACHO, logo abaixo.
           */
          const fired = new Set<number>();
          for (let i = 0; i < pad.buttons.length; i++) {
            const isPressed = Boolean(pad.buttons[i]?.pressed);
            if (isPressed && !(prevButtons.get(i) ?? false)) fired.add(i);
            prevButtons.set(i, isPressed);
          }

          if (enabledRef.current) {
            const cb = callbacksRef.current;

            // --- 1. Direcionais (D-Pad Digital + Analógico Esquerdo) ---
            let dx = 0;
            let dy = 0;

            // D-Pad buttons: 12 (cima), 13 (baixo), 14 (esquerda), 15 (direita)
            if (pad.buttons[12]?.pressed) dy -= 1;
            if (pad.buttons[13]?.pressed) dy += 1;
            if (pad.buttons[14]?.pressed) dx -= 1;
            if (pad.buttons[15]?.pressed) dx += 1;

            // Suporte suplementar para D-Pad em eixos POV (controles DirectInput / Linux joydev)
            if (dx === 0 && dy === 0) {
              if (typeof pad.axes[6] === 'number' && Math.abs(pad.axes[6]) > DEADZONE) {
                dx = pad.axes[6] > 0 ? 1 : -1;
              }
              if (typeof pad.axes[7] === 'number' && Math.abs(pad.axes[7]) > DEADZONE) {
                dy = pad.axes[7] > 0 ? 1 : -1;
              }
            }

            // Analógico Esquerdo (Axes 0 e 1)
            const axisX = pad.axes[0] ?? 0;
            const axisY = pad.axes[1] ?? 0;

            if (dx === 0) {
              if (axisX > DEADZONE) dx = 1;
              else if (axisX < -DEADZONE) dx = -1;
            }
            if (dy === 0) {
              if (axisY > DEADZONE) dy = 1;
              else if (axisY < -DEADZONE) dy = -1;
            }

            // Controle de repetição
            if (dx !== 0 || dy !== 0) {
              const dirChanged = dx !== lastDirX || dy !== lastDirY;
              if (dirChanged) {
                lastDirX = dx;
                lastDirY = dy;
                nextRepeatTime = now + INITIAL_REPEAT_MS;
                cb.onDirection?.(dx, dy);
              } else if (now >= nextRepeatTime) {
                nextRepeatTime = now + REPEAT_INTERVAL_MS;
                cb.onDirection?.(dx, dy);
              }
            } else {
              lastDirX = 0;
              lastDirY = 0;
              nextRepeatTime = 0;
            }

            // --- 2. Botões de Ação (Edge-triggered / transição de solto para pressionado) ---
            const checkButton = (index: number, action?: () => void) => {
              if (fired.has(index)) action?.();
            };

            /*
             * A tela que só tem texto não pergunta qual botão foi.
             *
             * Numa caixa de fala não há escolha a fazer, e portanto nada que um
             * botão possa cancelar: os quatro de rosto avançam. Quem declara
             * isto não declara confirmar/cancelar — senão o mesmo toque faria
             * as duas coisas —, e é por isso que o despacho dos dois fica no
             * ramo contrário.
             */
            if (cb.onAnyFaceButton) {
              for (const index of [0, 1, 2, 3]) {
                if (fired.has(index)) {
                  cb.onAnyFaceButton();
                  break;
                }
              }
            }

            // Mapeamento de botões de ação:
            // Standard Gamepad:
            //   0: Botão inferior (Xbox A / Switch B)
            //   1: Botão direito  (Xbox B / Switch A)
            //   2: Botão esquerdo (Xbox X / Switch Y)
            //   3: Botão superior (Xbox Y / Switch X)
            /*
             * Confirmar e cancelar, pela posição do botão.
             *
             * Os índices da Gamepad API são posicionais em qualquer controle:
             * 0 embaixo, 1 à direita, 2 à esquerda, 3 em cima. Quem decide qual
             * deles confirma é a preferência do jogador — antes isto era
             * deduzido do NOME do controle, e um adaptador genérico que se
             * anunciasse como Nintendo trocava os dois botões sem aviso: apertar
             * confirmar cancelava e vice-versa.
             */
            const confirmIndex = confirmRef.current === 'direita' ? 1 : 0;
            const cancelIndex = confirmRef.current === 'direita' ? 0 : 1;

            if (!cb.onAnyFaceButton) {
              checkButton(confirmIndex, () => cb.onConfirm?.());
              checkButton(cancelIndex, () => cb.onCancel?.());

              // Os dois de cima e da esquerda percorrem alvos, nos dois costumes.
              checkButton(2, () => cb.onCycleTarget?.(currentLayout === 'nintendo' ? -1 : 1));
              checkButton(3, () => cb.onCycleTarget?.(currentLayout === 'nintendo' ? 1 : -1));
            }

            // LB / L1 (4) -> Ciclo de unidades aliadas ou alvos (reverso)
            checkButton(4, () => {
              cb.onCycleAlly?.(-1);
              cb.onCycleTarget?.(-1);
            });

            // RB / R1 (5) -> Ciclo de unidades aliadas ou alvos (avanço)
            checkButton(5, () => {
              cb.onCycleAlly?.(1);
              cb.onCycleTarget?.(1);
            });

            // LT / L2 (6) -> Afasta a câmera; RT / R2 (7) -> aproxima.
            // Nos Fire Emblem de GameCube o afastamento é dos gatilhos, e a
            // memória muscular de quem jogou aqueles vem junto.
            checkButton(6, () => cb.onZoomStep?.(-1));
            checkButton(7, () => cb.onZoomStep?.(1));

            // Select / Share / Back / Minus (8) -> Mutar áudio
            checkButton(8, () => {
              cb.onToggleMute?.();
            });

            /*
             * L3 / clique do analógico esquerdo (10) -> Alcance inimigo.
             *
             * Sobrou o polegar que já está no analógico: é a mão que anda com
             * o cursor pelo mapa, e acender a mancha vermelha é justamente o
             * que se faz no meio dessa varredura. Os botões de rosto e os
             * gatilhos já têm dono, e o alcance de UM inimigo continua saindo
             * do confirmar em cima dele.
             */
            checkButton(10, () => {
              cb.onToggleThreat?.();
            });

            // Start / Options / Plus (9) -> Encerrar fase ou Pular combate
            checkButton(9, () => {
              cb.onEndPhase?.();
              cb.onSkipCombat?.();
            });
          }
        }
      }

      animId = requestAnimationFrame(poll);
    };

    animId = requestAnimationFrame(poll);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('gamepadconnected', onConnected);
      window.removeEventListener('gamepaddisconnected', onDisconnected);
    };
  }, []);

  return { isConnected, gamepadName, layout };
}
